import { Controller } from "@hotwired/stimulus";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.min.mjs";

// Connects to data-controller="pdf-renderer"
// Base controller for rendering PDFs to HTML canvas
// Can be extended by other controllers for specific functionality
export default class extends Controller {
  static targets = ["canvas", "pagePreview", "existingPdfs"];

  connect() {
    this.renderedFiles = new Set();
    this.existingFiles = new Set(); // Track files loaded from existing PDFs
    this.uploadedFiles = new Set(); // Track files loaded from file picker
  }

  // Upload PDF and Render on Canvas
  load_pdfs(event) {
    const files = [...event.target.files];

    // Remove previously uploaded PDFs (but keep existing/async-loaded ones)
    this.remove_uploaded_pdfs();

    files.forEach((file) => {
      if (file && file.type === "application/pdf" && !this.renderedFiles.has(file.name)) {
        this.renderedFiles.add(file.name);
        this.uploadedFiles.add(file.name); // Track as uploaded file
        this.on_pdf_loaded(file);

        const reader = new FileReader();
        reader.onload = (e) => {
          const pdf_bytes = new Uint8Array(e.target.result);
          this.render_pdf(pdf_bytes, file.name);
        };
        reader.readAsArrayBuffer(file);
      }
    });
  }

  async load_existing_pdfs() {
    if (!this.hasExistingPdfsTarget) return;

    const files = JSON.parse(this.existingPdfsTarget.dataset.existingPdfs || "[]");

    for (const file of files) {
      if (this.renderedFiles.has(file.name)) continue;

      this.renderedFiles.add(file.name);
      this.existingFiles.add(file.name); // Track as existing file

      // Enable tagging, same as file upload
      this.on_pdf_loaded({ name: file.name });

      const response = await fetch(file.url);
      const buffer = await response.arrayBuffer();
      const pdf_bytes = new Uint8Array(buffer);

      await this.render_pdf(pdf_bytes, file.name);
    }
  }

  // Hook method - override in subclasses to handle PDF loaded event
  on_pdf_loaded(_file) {
    // Default implementation does nothing
    // Subclasses can override this to enable buttons, show UI, etc.
  }

  // Remove PDFs that were uploaded via file picker (but keep existing/async-loaded ones)
  remove_uploaded_pdfs() {
    this.uploadedFiles.forEach((fileName) => {
      // Remove from canvas (pdf-wrapper)
      const pdfWrapper = this.canvasTarget.querySelector(`[data-file-name="${fileName}"]`);
      if (pdfWrapper) {
        pdfWrapper.remove();
      }

      // Remove from preview (pdf-preview-container)
      const previewContainer = this.pagePreviewTarget.querySelector(
        `.pdf-preview-container[data-file-name="${fileName}"]`,
      );
      if (previewContainer) {
        previewContainer.remove();
      }

      // Remove from tracking sets
      this.renderedFiles.delete(fileName);
    });

    // Clear uploaded files set
    this.uploadedFiles.clear();
  }

  create_pdf_wrapper(file_name) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("pdf-wrapper");
    wrapper.dataset.fileName = file_name;
    this.canvasTarget.appendChild(wrapper);
    return wrapper;
  }

  async render_page_canvas(page, scale) {
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext("2d");
    await page.render({ canvasContext: context, viewport }).promise;
    return { canvas, viewport };
  }

  add_file_information(file_name, preview_container, num_pages) {
    const preview_file_name = document.createElement("p");
    preview_file_name.innerText = file_name;
    preview_file_name.tabIndex = -1; // Make it focusable

    preview_container.append(preview_file_name);
    const preview_file_page_num = document.createElement("small");
    preview_file_page_num.innerText = `pages: ${num_pages}`;
    preview_container.append(preview_file_page_num);
  }

  async render_pdf(pdf_data, file_name) {
    const pdf = await pdfjsLib.getDocument({ data: pdf_data }).promise;
    const num_pages = pdf.numPages;
    const scale = this.get_pdf_scale();

    const pdf_wrapper = this.create_pdf_wrapper(file_name);
    pdf_wrapper.dataset.totalHeight = "0";

    // Create a container for this PDF's thumbnails
    const preview_container = document.createElement("div");
    preview_container.classList.add("pdf-preview-container");
    preview_container.dataset.fileName = file_name;
    this.add_file_information(file_name, preview_container, num_pages);

    for (let page_num = 1; page_num <= num_pages; page_num++) {
      const page = await pdf.getPage(page_num);
      const { canvas, viewport } = await this.render_page_canvas(page, scale);
      pdf_wrapper.appendChild(canvas);

      // Create thumbnail and add to PDF-specific container
      const page_preview = await this.render_page_preview(canvas, page, file_name, page_num);
      preview_container.appendChild(page_preview);

      // Hook for subclasses to add additional elements per page
      await this.on_page_rendered(page, canvas, viewport, page_num, pdf_wrapper, scale);
    }

    this.pagePreviewTarget.appendChild(preview_container);
  }

  // Hook method - override in subclasses to handle each rendered page
  // This is where document_tagging can add overlays, for example
  async on_page_rendered(_page, _canvas, _viewport, _page_num, _pdf_wrapper, _scale) {
    // Default implementation does nothing
    // Subclasses can override to add overlays, annotations, etc.
  }

  async render_page_preview(canvas, page, file_name, page_num) {
    const thumb_scale = this.get_thumbnail_scale();
    const { canvas: thumb_canvas } = await this.render_page_canvas(page, thumb_scale);

    const thumb_wrapper = document.createElement("div");
    thumb_wrapper.id = `${file_name}-${page_num}`;
    thumb_wrapper.classList.add("page-preview-item");
    thumb_wrapper.dataset.pageNumber = page_num;
    thumb_wrapper.tabIndex = 0; // Make it focusable
    thumb_wrapper.appendChild(thumb_canvas);

    // Click handler
    thumb_wrapper.addEventListener("click", () => {
      this.select_thumbnail(thumb_wrapper);
      canvas.scrollIntoView({ behavior: "smooth" });
    });

    // Keyboard navigation
    thumb_wrapper.addEventListener("keydown", (e) => {
      this.handle_thumbnail_keyboard_navigation(e, thumb_wrapper);
    });

    return thumb_wrapper;
  }

  select_thumbnail(thumbnail) {
    this.pagePreviewTarget
      .querySelectorAll(".selected")
      .forEach((el) => el.classList.remove("selected"));

    thumbnail.classList.add("selected");
    thumbnail.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  handle_thumbnail_keyboard_navigation(event, current_thumbnail) {
    // Only handle arrow keys
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) {
      return;
    }

    event.preventDefault();

    // Get all thumbnails in the current PDF preview container
    const container = current_thumbnail.closest(".pdf-preview-container");
    const thumbnails = Array.from(container.querySelectorAll(".page-preview-item"));
    const current_index = thumbnails.indexOf(current_thumbnail);

    let next_index;
    if (event.key === "ArrowDown") {
      next_index = Math.min(current_index + 1, thumbnails.length - 1);
    } else if (event.key === "ArrowUp") {
      next_index = Math.max(current_index - 1, 0);
    }

    if (next_index !== undefined && next_index !== current_index) {
      const next_thumbnail = thumbnails[next_index];

      // Select and focus the next thumbnail
      this.select_thumbnail(next_thumbnail);
      next_thumbnail.focus();

      // Scroll the corresponding page into view
      const page_num = parseInt(next_thumbnail.dataset.pageNumber);
      const file_name = container.dataset.fileName;
      const pdf_wrapper = this.canvasTarget.querySelector(`[data-file-name="${file_name}"]`);
      const canvases = pdf_wrapper?.querySelectorAll("canvas");
      const page_canvas = canvases?.[page_num - 1];

      if (page_canvas) {
        page_canvas.scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  // Utility method
  get_root_font_size() {
    return parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  // Configuration methods - can be overridden in subclasses
  get_pdf_scale() {
    return 1.5;
  }

  get_thumbnail_scale() {
    return 0.3;
  }
}
